import React from 'react';
import { Input } from 'components/ui/input';
import { Download, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Combobox } from 'components/ui/combobox';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Calendar } from 'components/ui/calendar';
import { cn } from 'lib/utils';
import Dropdowns from 'components/Dropdowns';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import {
  fetchPendingPodCustomers,
  fetchPendingPodData,
  fetchPendingPodVendors,
  PendingPodLevel,
  PendingPodLookupOption,
  PendingPodRow,
  updatePendingPodDropdowns,
  downloadPendingPods,
  PendingPodStatus
} from './api';
import PendingPODTable from './components/PendingPODTable';

type SortDirection = 'asc' | 'desc';

type ComboboxOption = {
  value: string;
  label: string;
};

const statusOptions: ComboboxOption[] = [
  {
    value: 'not_due',
    label: 'Not Due',
  },
  {
    value: 'overdue',
    label: 'Overdue',
  },
];

const toDateValue = (value?: string) => {
  if (!value) return undefined;
  return new Date(`${value}T00:00:00`);
};

const toApiDate = (value?: Date) => {
  if (!value) return '';
  return format(value, 'yyyy-MM-dd');
};

const PendingPodReportPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumbs();
  const [levels, setLevels] = React.useState<PendingPodLevel[]>([]);
  const [dropdownLoading, setDropdownLoading] = React.useState<boolean>(false);
  const [dropdownError, setDropdownError] = React.useState<string>('');
  const [lookupLoading, setLookupLoading] = React.useState<boolean>(false);
  const [customerOptions, setCustomerOptions] = React.useState<ComboboxOption[]>([]);
  const [vendorOptions, setVendorOptions] = React.useState<ComboboxOption[]>([]);
  const [search, setSearch] = React.useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = React.useState<string>('');
  const [selectedCustomer, setSelectedCustomer] = React.useState<string>('');
  const [selectedVendor, setSelectedVendor] = React.useState<string>('');
  const [selectedStatus, setSelectedStatus] = React.useState<PendingPodStatus | ''>('');
  const [fromDate, setFromDate] = React.useState<string>('');
  const [toDate, setToDate] = React.useState<string>('');
  const [dateOpen, setDateOpen] = React.useState<boolean>(false);
  const [dateTarget, setDateTarget] = React.useState<'from' | 'to'>('from');
  const [tableRows, setTableRows] = React.useState<PendingPodRow[]>([]);
  const [tableLoading, setTableLoading] = React.useState<boolean>(false);
  const [tableError, setTableError] = React.useState<string>('');
  const [downloadingExcel, setDownloadingExcel] = React.useState<boolean>(false);
  const [page, setPage] = React.useState<number>(1);
  const [perPage, setPerPage] = React.useState<number>(20);
  const [totalItems, setTotalItems] = React.useState<number>(0);
  const [sortBy, setSortBy] = React.useState<keyof PendingPodRow>('cn_number');
  const [sortOrder, setSortOrder] = React.useState<SortDirection>('asc');

  const buildFilters = React.useCallback((currentLevels: PendingPodLevel[]) => {
    const filters: Record<string, string[]> = {};
    currentLevels.forEach((level) => {
      if (level.selected && level.selected.length > 0) {
        filters[String(level.id)] = level.selected;
      }
    });
    return filters;
  }, []);

  const selectedFiltersKey = React.useMemo(() => JSON.stringify(buildFilters(levels)), [buildFilters, levels]);
  const selectedFilters = React.useMemo(
    () => JSON.parse(selectedFiltersKey || '{}') as Record<string, string[]>,
    [selectedFiltersKey]
  );

  const fetchDropdowns = React.useCallback(async (selection: Record<string, string[]> = {}) => {
    setDropdownLoading(true);
    setDropdownError('');
    try {
      const resp = await updatePendingPodDropdowns({ selection });
      setLevels(Array.isArray(resp?.levels) ? resp.levels : []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load filters';
      setDropdownError(message);
      setLevels([]);
    } finally {
      setDropdownLoading(false);
    }
  }, []);

  const fetchCustomers = React.useCallback(async (selection: Record<string, string[]> = {}, vendorCode?: string) => {
      try {
        const resp = await fetchPendingPodCustomers({
          filters: selection,
          vendor_code: vendorCode,
        });

        const options = (resp?.items ?? []).map((item) => ({
          value: item.code,
          label: item.name,
        }));
        setCustomerOptions(options);
        // clear a selection that's no longer in the refetched list
        const codes = new Set(options.map((o) => o.value));
        setSelectedCustomer((prev) => (prev && !codes.has(prev) ? '' : prev));
      } catch {
        setCustomerOptions([]);
      }
    },
    []
  );

  const fetchVendors = React.useCallback(async (selection: Record<string, string[]> = {}, custCode?: string) => {
      try {
        const resp = await fetchPendingPodVendors({
          filters: selection,
          cust_code: custCode,
        });

        const options = (resp?.items ?? []).map((item) => ({
          value: item.code,
          label: item.name,
        }));
        setVendorOptions(options);
        // clear a selection that's no longer in the refetched list
        const codes = new Set(options.map((o) => o.value));
        setSelectedVendor((prev) => (prev && !codes.has(prev) ? '' : prev));
      } catch {
        setVendorOptions([]);
      }
    }, []);

  const fetchTableData = React.useCallback(async (
    currentFilters: Record<string, string[]>,
    pageOverride?: number,
    sortOverride?: keyof PendingPodRow,
    orderOverride?: SortDirection,
    perPageOverride?: number,
  ) => {
    setTableLoading(true);
    setTableError('');
    try {
      const resp = await fetchPendingPodData({
        filters: currentFilters,
        search: debouncedSearch.trim() || undefined,
        cust_code: selectedCustomer || undefined,
        vendor_code: selectedVendor || undefined,
        status: selectedStatus || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        page: pageOverride ?? page,
        per_page: perPageOverride ?? perPage,
        sort_by: String(sortOverride ?? sortBy),
        sort_order: orderOverride ?? sortOrder,
      });

      setTableRows(Array.isArray(resp?.rows) ? resp.rows : []);
      setTotalItems(Number(resp?.total_items || 0));
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to fetch report data';
      setTableError(message);
      setTableRows([]);
      setTotalItems(0);
    } finally {
      setTableLoading(false);
    }
  }, [debouncedSearch, page, perPage, selectedCustomer, selectedVendor, selectedStatus, fromDate, toDate, sortBy, sortOrder]);

  const handleDownloadExcel = React.useCallback(async () => {
    try {
      setDownloadingExcel(true);
      setTableError('');

      const blob = await downloadPendingPods({
        filters: selectedFilters,
        search: debouncedSearch.trim() || undefined,
        cust_code: selectedCustomer || undefined,
        vendor_code: selectedVendor || undefined,
        status: selectedStatus || undefined,
        from_date: fromDate || undefined,
        to_date: toDate || undefined,
        sort_by: String(sortBy),
        sort_order: sortOrder,
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'pending_pods.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to download Excel';
      setTableError(message);
    } finally {
      setDownloadingExcel(false);
    }
  }, [selectedFilters, debouncedSearch, selectedCustomer, selectedVendor, selectedStatus, fromDate, toDate, sortBy, sortOrder]);

  React.useEffect(() => {
    setBreadcrumbs([
      { label: 'Reports', href: '/reports/pnl' },
      { label: 'Pending PODs' },
    ]);
  }, [setBreadcrumbs]);

  React.useEffect(() => {
    // initial load
    setPage(1);
    setLevels([]);
    setSearch('');
    setDebouncedSearch('');
    setSelectedCustomer('');
    setSelectedVendor('');
    setSelectedStatus('');
    setFromDate('');
    setToDate('');
    fetchDropdowns({});
    setLookupLoading(true);
      Promise.all([
        fetchCustomers({}),
        fetchVendors({}),
      ]).finally(() => setLookupLoading(false));
  }, [fetchDropdowns, fetchCustomers, fetchVendors]);

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 400);

    return () => window.clearTimeout(timer);
  }, [search]);

  React.useEffect(() => {
    fetchTableData(selectedFilters, page, sortBy, sortOrder, perPage);
  }, [debouncedSearch, selectedCustomer, selectedVendor, selectedStatus, fromDate, toDate, selectedFilters, page, perPage, sortBy, sortOrder, fetchTableData]);

  const handleSelectChange = React.useCallback((levelId: number | string, value: string[]) => {
    // changing a level clears all lower (descendant) selections (mirrors PnlPage)
    const desc = (levels || [])
      .filter((l) => Boolean(l?.visible))
      .sort((a, b) => Number(b.id) - Number(a.id));
    const indexById = Object.fromEntries(desc.map((l, i) => [String(l.id), i]));
    const changedIdx = indexById[String(levelId)];

    const currentLevels = levels.map((level) => {
      const idx = indexById[String(level.id)];
      if (idx === changedIdx) {
        return { ...level, selected: value.length > 0 ? value : null };
      }
      if (idx > changedIdx) {
        return { ...level, selected: null };
      }
      return level;
    });

    setLevels(currentLevels);
    const selection = buildFilters(currentLevels);
    fetchDropdowns(selection);

    setLookupLoading(true);
    Promise.all([
      fetchCustomers(selection, selectedVendor || undefined),
      fetchVendors(selection, selectedCustomer || undefined),
    ]).finally(() => setLookupLoading(false));
    setPage(1);
  }, [buildFilters, fetchDropdowns, fetchCustomers, fetchVendors, selectedCustomer, selectedVendor, levels]);

  const toggleSort = React.useCallback((column: keyof PendingPodRow) => {
    const nextOrder: SortDirection = sortBy === column && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(column);
    setSortOrder(nextOrder);
    setPage(1);
  }, [sortBy, sortOrder]);

  const date = React.useMemo(() => ({
    from: toDateValue(fromDate),
    to: toDateValue(toDate),
  }), [fromDate, toDate]);

  const handleDateSelect = (selectedDate?: Date) => {
    if (!selectedDate) return;

    if (dateTarget === 'from') {
      setFromDate(toApiDate(selectedDate));
      if (date.to && selectedDate > date.to) {
        setToDate('');
      }
    } else {
      setToDate(toApiDate(selectedDate));
    }
    setDateOpen(false);
    setPage(1);
  };

  const handlePageChange = React.useCallback((nextPage: number) => {
    const safePage = Math.max(1, nextPage);
    setPage(safePage);
  }, []);
  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-screen-2xl flex-col gap-6 px-4 sm:px-6 2xl:max-w-[2000px]">
      <div className="shrink-0 space-y-4">
        <Dropdowns
          showYear={false}
          levels={levels}
          loading={dropdownLoading}
          onSelectChange={handleSelectChange}
        />

        {dropdownError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-600">{dropdownError}</p>
          </div>
        )}
      </div>

      <div className="shrink-0 grid gap-4 lg:grid-cols-[1fr_auto_1fr_1fr_1fr_auto] items-end">
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Search CN No.</label>
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Type CN number..."
          />
        </div>
        
        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Delivery Date Range</label>
          <div className="flex items-center border rounded-md bg-background h-10">
            <Popover open={dateOpen} onOpenChange={setDateOpen}>
              <div className="flex divide-x">
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDateTarget('from');
                      setDateOpen(true);
                    }}
                    className={cn(
                      'h-10 px-2 py-1 justify-start text-left font-normal rounded-none rounded-l-md hover:bg-muted/50 text-xs min-w-[145px]',
                      !date.from && 'text-gray-500'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />
                    <div className="flex flex-col min-w-0 leading-tight">
                      <span className="text-xs font-normal text-gray-600">From Date</span>
                      <span className="text-xs truncate">{date.from ? format(date.from, 'dd MMM yyyy') : 'Start'}</span>
                    </div>
                  </Button>
                </PopoverTrigger>

                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setDateTarget('to');
                      setDateOpen(true);
                    }}
                    className={cn(
                      'h-10 px-2 py-1 justify-start text-left font-normal rounded-none rounded-r-md hover:bg-muted/50 text-xs min-w-[145px]',
                      !date.to && 'text-gray-500'
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3 w-3 flex-shrink-0" />
                    <div className="flex flex-col min-w-0 leading-tight">
                      <span className="text-xs font-normal text-gray-600">To Date</span>
                      <span className="text-xs truncate">{date.to ? format(date.to, 'dd MMM yyyy') : 'Latest'}</span>
                    </div>
                  </Button>
                </PopoverTrigger>
              </div>

              <PopoverContent className="w-auto p-0 max-w-fit" align="start">
                <div className="p-3">
                  <div className="text-sm font-semibold mb-3 text-gray-900">{dateTarget === 'from' ? 'Select start date' : 'Select end date'}</div>

                  <Calendar
                    mode="single"
                    selected={dateTarget === 'from' ? date.from : date.to}
                    onSelect={handleDateSelect}
                    numberOfMonths={2}
                    disabled={(d) => {
                      if (dateTarget === 'to' && date.from) {
                        return d < date.from;
                      }
                      return false;
                    }}
                    className="text-sm"
                    classNames={{
                      months: 'flex flex-row gap-4',
                      month: 'space-y-2',
                      caption: 'flex justify-center pt-1 relative items-center text-sm',
                      table: 'w-full border-collapse',
                      head_row: 'flex',
                      head_cell: 'text-muted-foreground rounded-md w-7 text-[11px]',
                      row: 'flex w-full mt-1',
                      cell: 'h-7 w-7 text-center text-xs p-0 relative',
                      day: 'h-7 w-7 p-0 text-xs',
                    }}
                  />

                  <div className="flex justify-between mt-4 pt-3 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setFromDate('');
                        setToDate('');
                        setPage(1);
                      }}
                    >
                      Clear
                    </Button>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDateOpen(false)}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Customer</label>
          <Combobox
            options={customerOptions}
            value={selectedCustomer}
            onValueChange={(value) => {
              setSelectedCustomer(value);
              setLookupLoading(true);
              fetchVendors(selectedFilters, value)
                .finally(() => setLookupLoading(false));
              setPage(1);
            }}
            placeholder={lookupLoading ? 'Loading customers...' : 'All customers'}
            searchPlaceholder="Search customer..."
            emptyText="No customer found."
            disabled={lookupLoading}
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Vendor</label>
          <Combobox
            options={vendorOptions}
            value={selectedVendor}
            onValueChange={(value) => {
              setSelectedVendor(value);
              setLookupLoading(true);
              fetchCustomers(selectedFilters, value)
                .finally(() => setLookupLoading(false));
              setPage(1);
            }}
            placeholder={lookupLoading ? 'Loading vendors...' : 'All vendors'}
            searchPlaceholder="Search vendor..."
            emptyText="No vendor found."
            disabled={lookupLoading}
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <Combobox
            options={statusOptions}
            value={selectedStatus}
            onValueChange={(value) => {
              setSelectedStatus(value as PendingPodStatus | '');
              setPage(1);
            }}
            placeholder="All Statuses"
            searchPlaceholder="Search status..."
            emptyText="No status found."
          />
        </div>

        <div className="flex justify-end">
          <button
            className="px-3 py-1 text-sm rounded-md border border-green-600 text-green-700 hover:bg-green-50 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleDownloadExcel}
            disabled={downloadingExcel}
          >
            <Download size={16} />
            {downloadingExcel ? 'Downloading...' : 'Excel'}
          </button>
        </div>
      </div>

      {tableError && (
        <div className="shrink-0 rounded-md border border-red-200 bg-red-50 p-3">
          <p className="text-sm text-red-600">{tableError}</p>
        </div>
      )}

      <div className="min-h-0 flex-1">
        <PendingPODTable
          data={tableRows}
          loading={tableLoading}
          page={page}
          pageSize={perPage}
          totalItems={totalItems}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={toggleSort}
          onPageChange={handlePageChange}
          onPageSizeChange={(size) => {
            setPerPage(size);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default PendingPodReportPage;
